/**
 * O convite para liberar o Assistente, mostrado quando o plano do workspace
 * não o inclui.
 *
 * O botão muda conforme quem está olhando, porque o caminho para resolver é
 * diferente: o super admin libera a feature no plano em dois cliques; o resto
 * do time depende de quem administra a conta. Um botão único mandaria metade
 * das pessoas para uma tela onde elas não podem fazer nada.
 */

import { useLocation } from "wouter";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/_core/hooks/useAuth";
import { cn } from "@/lib/utils";
import { BENEFICIOS } from "./propaganda";

const ASSUNTO = "Liberar o Assistente no nosso plano";
const CORPO = [
  "Olá!",
  "",
  "Quero liberar o Assistente do Lead Conversa para o nosso workspace.",
  "Ele cria campanhas, fluxos e e-mails por conversa, responde sobre o funil e trabalha na tela em que a gente está.",
  "",
  "Pode me dizer como fazer a troca de plano?",
].join("\n");

export function BotaoUpgrade({ className, size = "sm" }: { className?: string; size?: "sm" | "default" }) {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const superAdmin = Boolean((user as { isSuperAdmin?: boolean } | null)?.isSuperAdmin);

  if (superAdmin) {
    return (
      <Button size={size} className={className} onClick={() => setLocation("/super-admin?tab=plans")}>
        Liberar no plano
        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
      </Button>
    );
  }

  return (
    <Button
      size={size}
      className={className}
      // `mailto:` sem destinatário: o cliente de e-mail abre com assunto e
      // texto prontos e a pessoa escolhe para quem manda. Preencher um
      // endereço aqui seria inventar um contato que o sistema não conhece.
      onClick={() => {
        window.location.href = `mailto:?subject=${encodeURIComponent(ASSUNTO)}&body=${encodeURIComponent(CORPO)}`;
      }}
    >
      Pedir liberação do plano
      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
    </Button>
  );
}

/** A vitrine inteira: o que o Assistente faz + o botão. */
export function CartaoUpgrade({ compacto, className }: { compacto?: boolean; className?: string }) {
  return (
    <div className={cn("rounded-xl border border-[color:var(--brand-accent)]/30 bg-[color:var(--brand-accent)]/5 p-4 text-left", className)}>
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 shrink-0 text-[color:var(--brand-accent)]" />
        <p className="text-sm font-semibold">O Assistente não está no seu plano</p>
      </div>
      <ul className={cn("mt-3 space-y-1.5 text-sm text-muted-foreground", compacto ? "" : "sm:columns-2 sm:gap-6")}>
        {BENEFICIOS.map((b) => (
          <li key={b} className="flex gap-2 break-inside-avoid">
            <span aria-hidden className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[color:var(--brand-accent)]" />
            <span className="leading-snug">{b}</span>
          </li>
        ))}
      </ul>
      <BotaoUpgrade className="mt-4" />
    </div>
  );
}
